import React from 'react';
import { useDispatch } from "react-redux";
import { setPageTitle, setStatusBarMessage } from '../../app/actions';
import axios from "axios";
import { getJwtToken } from '../Common/Auth';
import { getApiURL } from '../Common/envVarsReader';
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Select from "@amzn/awsui-components-react/polaris/select";
import Input from "@amzn/awsui-components-react/polaris/input";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Button from "@amzn/awsui-components-react/polaris/button";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Box from "@amzn/awsui-components-react/polaris/box";

// ===== NOTIFICATION TEMPLATES =====
const TEMPLATES = {
    monthly: {
        label: 'Monthly ASB Notification',
        description: 'Announce new ASB filing and Jira/Shepherd creation',
        generate: (vars) => {
            const month = vars.month || '<Month>';
            const year = vars.year || '<Year>';
            const wikiLink = vars.wikiLink || '<Wiki Link>';
            return `:bell: Android Security Bulletin (ASB) - ${month} ${year}

The ${month} ${year} Android Security Bulletin has been filed and processed.
• All applicable Jiras & Shepherd have been created and assigned to respective teams
• Bulletin Wiki page has been created for tracking: ${wikiLink}

Questions? Please contact suhsmith@ or leave questions on #sbs-asb-interest`;
        }
    },
    cve_revision: {
        label: 'Mid-Month CVE Revision',
        description: 'Announce CVE removals/additions from mid-month bulletin update',
        generate: (vars) => {
            const month = vars.month || '<Month>';
            const year = vars.year || '<Year>';
            const version = vars.version || 'vX.X';
            const date = vars.date || '<Date>';
            const cveContent = vars.cveContent || `CVE Removals (N)

<Category> (N CVEs):
• CVE-YYYY-NNNNN (A-XXXXXXXXX) - <Reason>`;
            const wikiLink = vars.wikiLink || '<Wiki Link>';
            return `:bell: Android Security Bulletin (ASB) - ${month} ${year} [Mid-Month update] ${version}

As of ${date}, here are the CVEs updates from ${month} ${year} ASB with their respective reasons:

${cveContent}

Partners may keep all removed fixes; no known regressions

• All applicable Jiras ticket's comment is added for those CVEs removal
• Bulletin Wiki page has been updated with details: ${wikiLink}

Questions? Please contact suhsmith@ or leave questions on #sbs-asb-interest`;
        }
    },
    emergency: {
        label: 'Emergency/Urgent Fix Required',
        description: 'Alert for critical vulnerabilities requiring immediate action',
        generate: (vars) => {
            const cveId = vars.cveId || 'CVE-XXXX-XXXXX';
            const title = vars.title || '<Vulnerability Title>';
            const summary = vars.summary || '<Summary of the issue and why action is required>';
            const cveDetails = vars.cveDetails || `• CVE: ${cveId}
• Severity: HIGH (CVSS X.X)
• Type: <Vulnerability Type>
• File: <Affected File/Component>
• Root Cause: <Brief root cause description>`;
            const devices = vars.devices || `• <device> (kernel X.X) — VULNERABLE → <Jira link>`;
            const nextSteps = vars.nextSteps || `1. Review your Jira ticket above for full details and patch links
2. Cherry-pick the required commit(s) into your device kernel tree
3. Verify the fix
4. Update your Shepherd ticket once the fix is merged and deployed`;
            return `:rotating_light: Emergent Fix Required — ${cveId} (${title})

Summary: ${summary}

CVE Details:
${cveDetails}

Affected Devices:
${devices}

:star: Next Steps for Teams:
${nextSteps}

Questions? Please contact suhsmith@ or leave questions on #sbs-asb-interest`;
        }
    }
};

function SendButton(props) {
    if (props.loading) {
        return <Spinner />;
    }
    if (props.disabled) {
        return (<Button disabled variant="primary">Send Notification</Button>);
    }
    return (<Button onClick={(e) => { e.preventDefault(); props.sendNotification(); }} variant="primary">Send Notification</Button>);
}

export default function SlackNotificationPage() {
    const dispatch = useDispatch();
    const [loading, setLoading] = React.useState(false);
    const [template, setTemplate] = React.useState(null);
    const [message, setMessage] = React.useState('');
    const [sent, setSent] = React.useState(false);

    React.useEffect(() => {
        dispatch(setPageTitle('Slack Notification'));
    }, []);

    const templateOptions = [
        { label: TEMPLATES.monthly.label, value: 'monthly', description: TEMPLATES.monthly.description },
        { label: TEMPLATES.cve_revision.label, value: 'cve_revision', description: TEMPLATES.cve_revision.description },
        { label: TEMPLATES.emergency.label, value: 'emergency', description: TEMPLATES.emergency.description },
    ];

    const onTemplateChange = (selectedOption) => {
        setTemplate(selectedOption);
        // Auto-populate message with template defaults
        const templateKey = selectedOption.value;
        if (TEMPLATES[templateKey]) {
            const now = new Date();
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
            const currentMonth = monthNames[now.getMonth()];
            const currentYear = now.getFullYear();
            const dateStr = `${monthNames[now.getMonth()]} ${now.getDate()}`;

            const vars = {
                month: currentMonth,
                year: currentYear,
                date: dateStr,
                version: 'vX.X',
                wikiLink: `https://wiki.labcollab.net/confluence/display/DIGSECPUB/${String(now.getMonth() + 1).padStart(2, '0')}+Android+Security+Bulletin+${currentMonth}+${currentYear}`,
            };
            setMessage(TEMPLATES[templateKey].generate(vars));
        }
        setSent(false);
    };

    const sendNotification = async () => {
        setLoading(true);
        dispatch(setStatusBarMessage(null));
        try {
            const options = {
                headers: {
                    'Authorization': `Bearer ${getJwtToken()}`,
                    'Content-Type': 'application/json'
                },
            };
            const data = {
                channel: 'sbs-asb-interest',
                message: message,
                template: template ? template.value : 'custom'
            };
            let response = await axios.post(`${getApiURL()}/notifications/slack`, data, options);
            dispatch(setStatusBarMessage({ type: 'success', message: 'Slack notification sent successfully!' }));
            setSent(true);
        } catch (e) {
            console.log(e);
            dispatch(setStatusBarMessage({ type: 'error', message: 'Failed to send notification: ' + e }));
        }
        setLoading(false);
    };

    return (
        <SpaceBetween size='l'>
            <Container
                header={
                    <Header variant="h2" description="Send a Slack notification to an ASB channel.">
                        Slack Notification
                    </Header>
                }
            >
                <SpaceBetween size="l">
                    <FormField label="Channel">
                        <Input
                            value="#sbs-asb-interest"
                            disabled={true}
                        />
                    </FormField>
                    <FormField label="Template" description="Select a notification template to auto-populate the message.">
                        <Select
                            selectedOption={template}
                            onChange={({ detail }) => onTemplateChange(detail.selectedOption)}
                            options={templateOptions}
                            placeholder="Choose a template"
                        />
                    </FormField>
                    {template && (
                        <Box variant="awsui-key-label" color="text-status-info">
                            Template: {template.label} — Edit the message below before sending.
                        </Box>
                    )}
                    <FormField label="Message" description="Review and edit the notification message before sending.">
                        <Textarea
                            onChange={({ detail }) => setMessage(detail.value)}
                            value={message}
                            rows={20}
                            placeholder="Select a template above to auto-populate, or type a custom message."
                        />
                    </FormField>
                </SpaceBetween>
            </Container>
            <div align="right">
                <SendButton
                    loading={loading}
                    disabled={!message || sent}
                    sendNotification={sendNotification}
                />
            </div>
        </SpaceBetween>
    );
}
